import { Filter, Search, X } from 'lucide-react';
import { useState } from 'react';
import './FilterBar.css';

const FilterBar = ({
    onFilterChange,
    totalCount,
    filteredCount,
    searchQuery = '',
    onSearchChange,
    activationFilter = '',
    onActivationFilterChange,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        hasTotem: '',
        minLocals: '',
        minWorkers: '',
        hideSuspended: true
    });

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const clearFilters = () => {
        const emptyFilters = {
            status: '',
            hasTotem: '',
            minLocals: '',
            minWorkers: '',
            hideSuspended: true
        };
        setFilters(emptyFilters);
        onFilterChange(emptyFilters);
    };

    const hasAdvancedFilters =
        filters.status !== '' ||
        filters.hasTotem !== '' ||
        filters.minLocals !== '' ||
        filters.minWorkers !== '' ||
        filters.hideSuspended === false;

    const activeFilterCount =
        (filters.status !== '' ? 1 : 0) +
        (filters.hasTotem !== '' ? 1 : 0) +
        (filters.minLocals !== '' ? 1 : 0) +
        (filters.minWorkers !== '' ? 1 : 0) +
        (filters.hideSuspended === false ? 1 : 0) +
        (activationFilter !== '' ? 1 : 0);

    const hasActiveFilters = hasAdvancedFilters || activationFilter !== '' || searchQuery.trim() !== '';

    const clearAllFilters = () => {
        clearFilters();
        onSearchChange?.('');
        onActivationFilterChange?.('');
    };

    return (
        <div className="filter-bar">
            <div className="filter-toolbar">
                <div className="filter-search">
                    <Search size={17} aria-hidden="true" />
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(event) => onSearchChange?.(event.target.value)}
                        placeholder="Cerca alias, azienda, email, telefono o ID..."
                        aria-label="Cerca tenant"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className="filter-search-clear"
                            onClick={() => onSearchChange?.('')}
                            title="Cancella ricerca"
                            aria-label="Cancella ricerca"
                        >
                            <X size={15} />
                        </button>
                    )}
                </div>

                <select
                    className="activation-filter-select"
                    value={activationFilter}
                    onChange={(event) => onActivationFilterChange?.(event.target.value)}
                    aria-label="Filtra per stato di attivazione"
                    title="In attivazione include tutti i tenant non-live. Bloccati esclude quelli pronti per il go-live."
                >
                    <option value="">Tutti gli stati di attivazione</option>
                    <option value="non_live">In attivazione</option>
                    <option value="blocked_operational">Bloccati</option>
                    <option value="blocked_week">Bloccati da almeno una settimana</option>
                    <option value="registered">Registrato</option>
                    <option value="onboarding_incomplete">Onboarding incompleto</option>
                    <option value="contract_pending">Contratto pending</option>
                    <option value="pending_soontip_approval">Pending approval</option>
                    <option value="approved_setup_required">Setup richiesto</option>
                    <option value="collection_channel_setup_required">Canale di incasso richiesto</option>
                    <option value="workers_setup_required">Worker richiesti</option>
                    <option value="ready_for_go_live">Ready for go-live</option>
                    <option value="live">Live</option>
                    <option value="suspended_closed">Suspended/Closed</option>
                </select>

                <button
                    type="button"
                    className="filter-toggle"
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-expanded={isExpanded}
                >
                    <Filter size={16} />
                    <span>Filtri</span>
                    {activeFilterCount > 0 && (
                        <span className="filter-badge">{activeFilterCount}</span>
                    )}
                </button>

                <div className="filter-results">
                    <span className="result-count">
                        {filteredCount} {filteredCount === 1 ? 'risultato' : 'risultati'}
                        {hasActiveFilters && ` di ${totalCount}`}
                    </span>
                    {hasActiveFilters && (
                        <button
                            type="button"
                            className="clear-filters"
                            onClick={clearAllFilters}
                        >
                            <X size={14} />
                            Rimuovi filtri
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="filter-controls">
                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">Tutti</option>
                            <option value="ACTIVE">Attivo</option>
                            <option value="CONTRACT_SIGNED_PENDING_PAYMENT">In Attesa Pagamento</option>
                            <option value="CONTRACT_SIGNED_PENDING_ACTIVATION">In Attesa Attivazione</option>
                            <option value="SUSPENDED">Sospeso</option>
                            <option value="ONBOARDING_STARTED">Onboarding</option>
                            <option value="EMAIL_VERIFIED">Email Verificata</option>
                            <option value="REGISTERED">Registrato</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Dispositivi</label>
                        <select
                            value={filters.hasTotem}
                            onChange={(e) => handleFilterChange('hasTotem', e.target.value)}
                        >
                            <option value="">Tutti</option>
                            <option value="yes">Con totem (≥1)</option>
                            <option value="no">Senza totem (0)</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Locali minimi</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="Es: 1"
                            value={filters.minLocals}
                            onChange={(e) => handleFilterChange('minLocals', e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Percettori minimi</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="Es: 5"
                            value={filters.minWorkers}
                            onChange={(e) => handleFilterChange('minWorkers', e.target.value)}
                        />
                    </div>

                    <div className="filter-group filter-group-checkbox">
                        <label className="filter-checkbox-card">
                            <input
                                type="checkbox"
                                checked={filters.hideSuspended}
                                onChange={(e) => handleFilterChange('hideSuspended', e.target.checked)}
                            />
                            <span>Nascondi Sospesi</span>
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterBar;
